import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Box, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { stripDropboxRootPrefix } from '../dropbox.utils';
import type { DropboxFolderSearchResultsProps } from './DropboxFolderSearchResults.props';

const DropboxFolderSearchResults = ({
  results,
  dropboxRoot,
  selectedPath,
  onSelect,
  isLoading = false,
  suggestedFolderName,
  maxHeight = 240,
}: DropboxFolderSearchResultsProps) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Searching folders...
        </Typography>
      </Box>
    );
  }

  if (results.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxHeight,
        overflow: 'auto',
      }}
    >
      <List dense disablePadding>
        {results.map(option => {
          const relativePath = stripDropboxRootPrefix(option.pathDisplay, [dropboxRoot]) || option.name;
          const isSelected = selectedPath === option.pathDisplay;
          const isSuggested = Boolean(
            suggestedFolderName?.toLowerCase() === option.name.toLowerCase()
          );

          return (
            <ListItemButton
              key={option.pathDisplay}
              selected={isSelected}
              onClick={() => onSelect(option)}
              data-testid={`dropbox-folder-result-${option.pathDisplay}`}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FolderOutlinedIcon fontSize="small" color={isSuggested ? 'primary' : 'action'} />
              </ListItemIcon>
              <ListItemText
                primary={option.name}
                secondary={relativePath}
                slotProps={{
                  primary: {
                    fontWeight: isSuggested ? 600 : 400,
                    color: isSuggested ? 'primary.main' : 'text.primary',
                  },
                  secondary: {
                    sx: { wordBreak: 'break-all' },
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default DropboxFolderSearchResults;
